import { BehaviorSubject, Observable, throwError, forkJoin } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, mergeMap, filter, first } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AgrirouterEndpoint } from '../_models/agrirouter/AgrirouterEndpoint';
import { AgrirouterFile } from '../_models/agrirouter/AgrirouterFile';
import { environment } from '../../../environments/environment';
import { AppState } from '../../common/_reducers';
import { AuthenticationService } from './authentication.service';
import { StoreModelService } from './storeModel.service';
import { StoreModelAction } from '../_models/storeModelAction';
import { SM } from '../../../assets/data/enum/storeModel.enum';
import { ThemeConfig } from '../../../assets/data/enum/themeConfig';
import { selectCurrentFarm } from '../_selectors/currentFarm.selectors';

function t<V extends string, T extends { [key in string]: V }>(o: T): T {
  return o;
}
export const AGRIROUTER_STATUSES = t({
  UNKNOWN: 'UNKNOWN',
  ERROR: 'ERROR',
  NOT_ALLOWED: 'NOT_ALLOWED',
  ALLOWED: 'ALLOWED',
  ONBOARDED: 'ONBOARDED',
  ONBOARDING_FAILED: 'ONBOARDING_FAILED',
  REVOKED: 'REVOKED',
  REVOKING_FAILED: 'REVOKING_FAILED',
  CONNECTION_FAILURE: 'CONNECTION_FAILURE',
});
export type AGRIROUTER_STATUSES_TYPE =
  (typeof AGRIROUTER_STATUSES)[keyof typeof AGRIROUTER_STATUSES];

@Injectable()
export class AgrirouterService {
  agrirouterInstanceStatus: BehaviorSubject<AGRIROUTER_STATUSES_TYPE> =
    new BehaviorSubject<AGRIROUTER_STATUSES_TYPE>(AGRIROUTER_STATUSES.UNKNOWN);
  ar_externalId: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private agrirouterFadeUrl = environment.agrirouterFadeUrl;
  private isoTaskUrl = environment.isoTaskManagementServiceUrl;
  private apiBaseUrl = environment.apiBaseUrl;
  private enableAgriRouterConnection: StoreModelAction = new StoreModelAction();
  private enableClaasConnection: StoreModelAction = new StoreModelAction();
  private enableClaasConnectConnection: StoreModelAction = new StoreModelAction();
  private enableCnhConnection: StoreModelAction = new StoreModelAction();
  private enableAllynavConnection: StoreModelAction = new StoreModelAction();
  private themeConfig = ThemeConfig;

  constructor(
    private store: Store<AppState>,
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private storeModelService: StoreModelService
  ) {
    this.store
      .select(selectCurrentFarm)
      .pipe(mergeMap((farm) => this.storeModelService.list.pipe(map((sm) => ({ farm, sm })))))
      .subscribe(({ farm, sm }) => {
        this.enableAgriRouterConnection =
          this.storeModelService.enableActionByStoreModelPROAndPermission(
            SM.SM_EQUIPMENT_TELEMETRY_AGRIROUTER
          );
        this.enableClaasConnection =
          this.storeModelService.enableActionByStoreModelPROAndPermission(
            SM.SM_EQUIPMENT_TELEMETRY_CLAAS
          );
        this.enableClaasConnectConnection =
          this.storeModelService.enableActionByStoreModelPROAndPermission(
            SM.SM_EQUIPMENT_TELEMETRY_CLAAS_CONNECT
          );
        this.enableCnhConnection = this.storeModelService.enableActionByStoreModelPROAndPermission(
          SM.SM_EQUIPMENT_TELEMETRY_CNHMAP
        );
        this.enableAllynavConnection =
          this.storeModelService.enableActionByStoreModelPROAndPermission(
            SM.SM_EQUIPMENT_TELEMETRY_ALLYNAV
          );

        if (
          farm &&
          (<any>farm).agrirouterUUID &&
          ((this.enableAgriRouterConnection.read && this.enableAgriRouterConnection.purchased) ||
            (this.enableClaasConnection.read && this.enableClaasConnection.purchased) ||
            (this.enableClaasConnectConnection.read &&
              this.enableClaasConnectConnection.purchased) ||
            (this.enableCnhConnection.read && this.enableCnhConnection.purchased) ||
            (this.enableAllynavConnection.read && this.enableAllynavConnection.purchased))
        ) {
          this.ar_externalId.next((<any>farm).agrirouterUUID);
          this.requestInstanceStatus().subscribe();
        } else {
          this.ar_externalId.next(null);
          this.agrirouterInstanceStatus.next(AGRIROUTER_STATUSES.UNKNOWN);
        }
      });
  }

  private sendRequest(url: string, options: any = {}): Observable<any> {
    const auth_tokens = this.authenticationService.getAuthToken();
    const headers = new HttpHeaders({
      Authorization: `Bearer ${auth_tokens.access_token}`,
      ...options.headers,
    });

    const body = options.body || null;

    return this.http
      .request(options.method || 'GET', url, {
        headers,
        body,
        observe: 'response',
        responseType: options.responseType || 'json',
      })
      .pipe(
        map((response) => response.body),
        catchError((err) => {
          return throwError(() => new Error('Request failed: ' + err.message));
        })
      );
  }

  private pollStatus(stopStatuses: AGRIROUTER_STATUSES_TYPE[]): void {
    const check = () => {
      this.requestInstanceStatus().subscribe((status) => {
        if (!stopStatuses.includes(status)) {
          setTimeout(check, 1000);
        }
      });
    };
    setTimeout(check, 1000);
  }

  getEndpoints(includePublish: boolean): Observable<AgrirouterEndpoint[]> {
    return this.ar_externalId.pipe(
      filter((externalId) => !!externalId),
      first(),
      mergeMap((externalId) =>
        this.sendRequest(
          this.agrirouterFadeUrl + 'webdav/data/' + externalId + '/ecosystem.json'
        ).pipe(
          map((json) => {
            const endpoints = json.communicationPartnerList
              .filter((endpoint) => endpoint.active === true)
              .map((endpoint) => AgrirouterEndpoint.fromFADEItem(endpoint));

            return includePublish
              ? [new AgrirouterEndpoint('PUBLISH', 'PUBLISH'), ...endpoints]
              : endpoints;
          })
        )
      )
    );
  }

  sendFileToAgrirouterEndpoints(
    fileName: string,
    fileContent: Blob,
    endpoints: AgrirouterEndpoint[]
  ): Observable<string[]> {
    return this.ar_externalId.pipe(
      filter((externalId) => !!externalId),
      first(),
      mergeMap((externalId) => {
        const outgoingPath = this.agrirouterFadeUrl + 'webdav/data/' + externalId + '/OUTGOING/';
        const endpointFolderUrls = endpoints.map((endpoint) => endpoint.id + '/' + fileName);
        return forkJoin(
          endpointFolderUrls.map((endpointFolderUrl) =>
            this.sendRequest(outgoingPath + endpointFolderUrl + '.part', {
              method: 'PUT',
              body: fileContent,
            }).pipe(map(() => 'Test'))
          )
        );
      })
    );
  }

  requestInstanceStatus(): Observable<AGRIROUTER_STATUSES_TYPE> {
    return this.ar_externalId.pipe(
      filter((externalId) => !!externalId),
      first(),
      mergeMap((externalId) =>
        this.sendRequest(
          this.agrirouterFadeUrl + 'webdav/data/' + externalId + '/status.json'
        ).pipe(
          map((jsonResponse) => {
            const newStatus = jsonResponse.status as AGRIROUTER_STATUSES_TYPE;
            this.agrirouterInstanceStatus.next(newStatus);
            return newStatus;
          }),
          catchError((err) => {
            if (err.status === 404) {
              this.agrirouterInstanceStatus.next(AGRIROUTER_STATUSES.ALLOWED);
            } else {
              this.agrirouterInstanceStatus.next(AGRIROUTER_STATUSES.ERROR);
            }
            return [this.agrirouterInstanceStatus.getValue()];
          })
        )
      )
    );
  }

  monitorOnboardingStatus(): void {
    this.pollStatus([
      AGRIROUTER_STATUSES.ONBOARDED,
      AGRIROUTER_STATUSES.ONBOARDING_FAILED,
      AGRIROUTER_STATUSES.ERROR,
      AGRIROUTER_STATUSES.CONNECTION_FAILURE,
    ]);
  }

  monitorRevokingStatus(): void {
    this.pollStatus([
      AGRIROUTER_STATUSES.REVOKED,
      AGRIROUTER_STATUSES.REVOKING_FAILED,
      AGRIROUTER_STATUSES.ERROR,
      AGRIROUTER_STATUSES.CONNECTION_FAILURE,
    ]);
  }

  isConnectedToAgrirouter(): boolean {
    return this.agrirouterInstanceStatus.getValue() === AGRIROUTER_STATUSES.ONBOARDED;
  }

  getExternalId(): string {
    return this.ar_externalId.getValue();
  }

  requestOnboardingURL(): Observable<string> {
    return this.ar_externalId.pipe(
      filter((externalId) => !!externalId),
      first(),
      mergeMap((externalId) =>
        this.sendRequest(this.agrirouterFadeUrl + 'webdav/admin/' + externalId + '.add').pipe(
          map((jsonResponse) => jsonResponse.url),
          catchError(() => {
            this.agrirouterInstanceStatus.next(AGRIROUTER_STATUSES.NOT_ALLOWED);
            return null;
          })
        )
      )
    );
  }

  requestRevoking(): Observable<string> {
    return this.ar_externalId.pipe(
      filter((externalId) => !!externalId),
      first(),
      mergeMap((externalId) =>
        this.sendRequest(this.agrirouterFadeUrl + 'webdav/admin/' + externalId + '.delete').pipe(
          map(() => {
            this.agrirouterInstanceStatus.next(AGRIROUTER_STATUSES.ALLOWED);
            return 'Request success'; // Restituisci un messaggio di successo
          }),
          catchError(() => throwError(new Error('Could not load status')))
        )
      )
    );
  }

  getFilesOfEndpoint(
    endpoint: AgrirouterEndpoint,
    externalSource: string
  ): Observable<AgrirouterFile[]> {
    return this.ar_externalId.pipe(
      filter((externalId) => !!externalId),
      first(),
      mergeMap((externalId) => {
        const url = `${this.isoTaskUrl}iso/taskdata/list/${externalId}/INCOMING/${endpoint.id}?externalSource=${externalSource}`;
        return this.sendRequest(url, { method: 'GET', responseType: 'text' }).pipe(
          map((text: string) => {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(text, 'application/xml');
            const items = Array.from(xmlDoc.getElementsByTagName('D:response'));

            const files = items.map((item) => {
              const date = item.getElementsByTagName('D:creationdate')[0].textContent;
              const filename = item.getElementsByTagName('D:displayname')[0].textContent;
              return { filename, date: new Date(date) };
            });

            return files.filter((file) => file.filename && file.filename.includes('.zip'));
          })
        );
      })
    );
  }

  downloadFile(
    endpointId: string,
    filename: string,
    externalSource: string
  ): Observable<{ data: Blob; fileType: string }> {
    return this.ar_externalId.pipe(
      filter((externalId) => !!externalId),
      first(),
      mergeMap((externalId) => {
        const url = `${this.isoTaskUrl}iso/taskdata/data/${externalId}/INCOMING/${endpointId}/${filename}?externalSource=${externalSource}`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
          map((response) => ({
            data: response.body as Blob,
            fileType: response.headers.get('Content-Type'),
          }))
        );
      })
    );
  }

  sendActivityToClaas(
    fieldActivityId: number,
    externalSource: string,
    equipmentId: number
  ): Observable<any> {
    return this.http.post(
      `${this.apiBaseUrl}api/private/v3/isoxml/fieldActivity/${fieldActivityId}/send?externalSource=${externalSource}&equipmentId=${equipmentId}`,
      null
    );
  }
}
